import { forwardRef, HTMLAttributes } from 'react';
import classNames from 'classnames';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'neutral';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  dot?: boolean;
}

/**
 * Badge component - Custom implementation replacing Flowbite
 *
 * Features:
 * - Multiple variants matching semantic colors
 * - Multiple sizes (sm, md, lg)
 * - Rounded pill style option
 * - Dot indicator option
 * - Dark mode support
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant = 'neutral',
      size = 'md',
      rounded = false,
      dot = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium transition-colors';

    const variantStyles = {
      primary:
        'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
      secondary:
        'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300',
      success:
        'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300',
      warning:
        'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300',
      error:
        'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-300',
      info: 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300',
      neutral:
        'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200',
    };

    const sizeStyles = {
      sm: 'px-2 py-0.5 text-xs gap-1',
      md: 'px-2.5 py-1 text-sm gap-1.5',
      lg: 'px-3 py-1.5 text-base gap-2',
    };

    const badgeClasses = classNames(
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      rounded ? 'rounded-full' : 'rounded',
      className
    );

    const dotClasses = classNames('w-1.5 h-1.5 rounded-full', {
      'bg-primary-600 dark:bg-primary-400': variant === 'primary',
      'bg-secondary-600 dark:bg-secondary-400': variant === 'secondary',
      'bg-success-600 dark:bg-success-400': variant === 'success',
      'bg-warning-600 dark:bg-warning-400': variant === 'warning',
      'bg-error-600 dark:bg-error-400': variant === 'error',
      'bg-info-600 dark:bg-info-400': variant === 'info',
      'bg-neutral-600 dark:bg-neutral-400': variant === 'neutral',
    });

    return (
      <span ref={ref} className={badgeClasses} {...props}>
        {dot && <span className={dotClasses} aria-hidden="true" />}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
